import { Link } from 'react-router-dom'
import { ToolkitPageLayout } from 'toolkit/components/ToolkitPageLayout'
import {
  AuditFeedbackGuideImage // Reused from clinics/audit-feedback assets
} from 'assets'

// TODO: Replace '#' with actual external URLs once documents are hosted.
const documentList = [
  {
    title: 'Patient Satisfaction Survey',
    fileName: 'Patient_Satisfaction_Survey_Institution.pdf',
    url: '#', // Was PatientSatisfactionSurveyInstitutionPdf
    category: 'Patient Satisfaction Survey Instrument'
  },
  {
    title: 'Pain Management and Patient Satisfaction Report',
    fileName: 'Pain_Management_Patient_Satisfaction_Report_Institution.pdf',
    url: '#', // Was PainManagementPatientSatisfactionReportInstitutionPdf
    category: 'Patient Satisfaction Survey Instrument'
  },
  {
    title: 'Hospital Outpatient Clinic Billing',
    fileName: 'Hospital_Outpatient_Clinic_Billing_Institution.pdf',
    url: '#', // Was HospitalOutpatientClinicBillingInstitutionPdf
    category: 'Infusion Clinic/Hospital Billing'
  },
  {
    title: 'IC Daily Data Collection Tool',
    fileName: 'IC_Daily_Data_Collection_Tool_Institution.xlsx',
    url: '#', // Was ICDailyDataCollectionToolInstitutionXlsx
    category: 'Data Collection Tools - Infusion Center',
    isXlsx: true
  },
  {
    title: 'Financial Tools for Infusion Clinic',
    fileName: 'Financial_Tools_Infusion_Clinic_Institution.pdf',
    url: '#', // Was FinancialToolsInfusionClinicInstitutionPdf
    category: 'Data Collection Tools - Financial Tools'
  },
  {
    title: 'Cost Effectiveness of Infusion Clinics',
    fileName: 'Cost_Effectiveness_Infusion_Clinics_Institution.pdf',
    url: '#', // Was CostEffectivenessInfusionClinicsInstitutionPdf
    category: 'Data Collection Tools - Financial Tools'
  },
  {
    title: 'ED Daily Data Collection Tool',
    fileName: 'ED_Daily_Data_Collection_Tool_Institution.xlsx',
    url: '#', // Was EDDailyDataCollectionToolInstitutionXlsx
    category: 'Data Collection Tools - ED Data Collection',
    isXlsx: true
  },
  {
    title: 'EPIC Report Build for Tracking ED Visits',
    fileName: 'EPIC_Report_Build_Institution.pdf',
    url: '#', // Was EPICReportBuildInstitutionPdf
    category: 'Data Collection Tools - ED Data Collection'
  }
]

interface DocumentItemProps {
  title: string
  fileName: string
  url: string
  isXlsx?: boolean
}

const DocumentDownloader: React.FC<DocumentItemProps> = ({
  title,
  fileName,
  url,
  isXlsx
}) => (
  <div className="mb-6 p-4 border border-gray-200 rounded-lg shadow-sm bg-gray-50">
    <h4 className="text-lg font-medium text-[#3C2416] mb-2">{title}</h4>
    <a
      href={url}
      download={fileName}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center px-4 py-2 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-[#A6410A] hover:bg-[#894d35] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#A6410A] transition-colors duration-200"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-5 w-5 mr-2"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path
          fillRule="evenodd"
          d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
          clipRule="evenodd"
        />
      </svg>
      Download {isXlsx ? 'XLSX' : 'PDF'}
    </a>
    {!isXlsx && url !== '#' && (
      <object
        data={url}
        type="application/pdf"
        width="100%"
        height="400px"
        className="mt-4 rounded shadow"
      >
        <p className="text-center text-sm text-gray-500 py-4">
          Your browser does not support inline PDFs. If the preview doesn&apos;t
          load, please use the download button.
        </p>
      </object>
    )}
    {url === '#' && !isXlsx && (
      <p className="text-xs text-gray-500 mt-2">
        <i>PDF preview will be available once the external link is provided.</i>
      </p>
    )}
  </div>
)

export const InstitutionAuditFeedbackPage: React.FC = () => {
  const breadcrumbs = [
    { label: 'Toolkit', path: '/toolkit' },
    { label: 'Institution', path: '/toolkit/institution' },
    {
      label: 'SCD Team & ED Relationship',
      path: '/toolkit/institution/scd-team-ed-relationship'
    },
    { label: 'Audit & Feedback', path: '/toolkit/institution/audit-feedback' }
  ]

  const groupedDocuments = documentList.reduce<
    Record<string, DocumentItemProps[]>
  >((acc, doc) => {
    if (!acc[doc.category]) {
      acc[doc.category] = []
    }
    acc[doc.category].push(doc)
    return acc
  }, {})

  return (
    <ToolkitPageLayout title="Audit & Feedback" breadcrumbs={breadcrumbs}>
      <div className="container mx-auto px-4 py-8 prose prose-lg max-w-4xl">
        <p className="lead text-center italic mb-10">
          The goal is feedback information to staff members in a structured,
          clear and concise manner based on data gathered prior to the audit.
          This will lead to sustained improvements and overall team building.
        </p>

        <hr className="my-12" />

        <h2 className="text-2xl font-semibold text-[#142841] mb-6">
          Read and Review Audit & Feedback Guide
        </h2>
        <div className="mb-12 text-center not-prose">
          <a
            href="https://docs.google.com/document/d/e/2PACX-1vQYuL3nmzV3fISUwl32AyncnIRDQpiDV9_j9yUqLfCU6bTWNnA7AsUKP_ilDt5oNw/pub"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block p-4 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 border border-gray-200 max-w-md"
          >
            <img
              src={AuditFeedbackGuideImage}
              alt="Infusion Center (IC) audit and feedback guide screenshot"
              className="w-full h-auto rounded-md shadow-sm"
            />
            <figcaption className="mt-3 text-md font-medium text-[#3C2416]">
              <em>Infusion Center (IC) audit and feedback</em>
            </figcaption>
          </a>
        </div>

        {Object.entries(groupedDocuments).map(([category, docs]) => (
          <section key={category} className="mb-12">
            <hr className="my-8" />
            <h3 className="text-xl font-semibold text-[#142841] mb-6">
              {category.startsWith('Data Collection Tools')
                ? category.substring(category.indexOf('-') + 2)
                : category}
            </h3>
            {category.startsWith('Data Collection Tools') && (
              <p className="text-sm text-gray-600 -mt-4 mb-4">
                Click the links below to download the tools.
              </p>
            )}
            <div className="not-prose">
              {docs.map((doc) => (
                <DocumentDownloader key={doc.title} {...doc} />
              ))}
            </div>
          </section>
        ))}

        <hr className="my-12" />
        <div className="mt-16 text-center not-prose">
          <Link
            to="/toolkit/institution/scd-team-ed-relationship"
            className="inline-block bg-[#A6410A] text-white font-semibold px-8 py-3 rounded-lg shadow-md hover:bg-[#C25F0C] transition-colors duration-300"
          >
            All done? Back to SCD Team & ED Relationship
          </Link>
        </div>
      </div>
    </ToolkitPageLayout>
  )
}
